import React, { useState, useEffect, useCallback, useRef } from "react";
import { api, runSync, SyncCancelledError } from "@/lib/api";
import { TID } from "@/constants/tids";
import { logError } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import {
  ShieldCheck,
  KeyRound,
  Upload,
  Loader2,
  ArrowRight,
  Lock,
  CheckCircle2,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Trash2,
  X,
  RefreshCw,
} from "lucide-react";
import logo from "@/assets/logo";
import ApiUsage from "@/components/ApiUsage";

// The 8 scopes SalesTap actually uses to run an analysis (highlighted as the
// minimum). Everything else is optional and only powers the CRM Inventory counts.
const MIN_SCOPES = new Set([
  "crm.objects.deals.read",
  "crm.objects.owners.read",
  "crm.objects.contacts.read",
  "crm.objects.calls.read",
  "crm.objects.meetings.read",
  "crm.objects.tasks.read",
  "crm.objects.notes.read",
  "sales-email-read",
]);

// Full list of supported read scopes. If a user grants any of these on their
// key, SalesTap can read/inventory the corresponding data. The 8 MIN_SCOPES are
// required for the core analysis; the rest are optional (power CRM Inventory).
const ALL_SCOPES = [
  // --- minimum for analysis (activity scopes included) ---
  "crm.objects.deals.read",
  "crm.objects.owners.read",
  "crm.objects.contacts.read",
  "crm.objects.calls.read",
  "crm.objects.meetings.read",
  "crm.objects.tasks.read",
  "crm.objects.notes.read",
  "sales-email-read",
  // --- optional CRM object scopes ---
  "crm.objects.companies.read",
  "crm.objects.leads.read",
  "crm.objects.line_items.read",
  "crm.objects.products.read",
  "crm.objects.quotes.read",
  "crm.objects.orders.read",
  "crm.objects.carts.read",
  "crm.objects.invoices.read",
  "crm.objects.subscriptions.read",
  "crm.objects.commercepayments.read",
  "crm.objects.contracts.read",
  "crm.objects.services.read",
  "crm.objects.courses.read",
  "crm.objects.listings.read",
  "crm.objects.appointments.read",
  "crm.objects.goals.read",
  "crm.objects.feedback_submissions.read",
  "crm.objects.marketing_events.read",
  "crm.objects.users.read",
  "crm.objects.custom.read",
  "crm.objects.partner-clients.read",
  "crm.objects.partner-services.read",
  "crm.objects.projects.read",
  // --- schema (property definition) scopes ---
  "crm.schemas.contacts.read",
  "crm.schemas.companies.read",
  "crm.schemas.deals.read",
  "crm.schemas.line_items.read",
  "crm.schemas.quotes.read",
  "crm.schemas.orders.read",
  "crm.schemas.invoices.read",
  "crm.schemas.subscriptions.read",
  "crm.schemas.commercepayments.read",
  "crm.schemas.contracts.read",
  "crm.schemas.services.read",
  "crm.schemas.courses.read",
  "crm.schemas.listings.read",
  "crm.schemas.carts.read",
  "crm.schemas.appointments.read",
  "crm.schemas.projects.read",
  "crm.schemas.custom.read",
  // --- specialized / non-object scopes ---
  "crm.lists.read",
  "crm.pipelines.orders.read",
  "timeline.read",
  "conversations.read",
  "automation.sequences.read",
  "crm.extensions_calling_transcripts.read",
  "marketing.aeo.read",
  "revenue.contracts.read",
];

// Instructions for each of the three HubSpot credential types (all accepted in
// the single field below). CSV upload is the 4th option, shown separately.
const METHODS = [
  {
    id: "private-app",
    title: "Private App access token",
    badge: "Recommended",
    link: "https://app.hubspot.com/l/private-apps",
    linkLabel: "Open Private Apps",
    steps: [
      "Settings (gear) → Integrations → Private Apps (may show under “Legacy apps”).",
      "Click “Create a private app” and name it (e.g. “Sales Analyst”).",
      "Scopes tab → add the read scopes listed below.",
      "Create app → Auth tab → “Show token” → copy the full token.",
    ],
  },
  {
    id: "service-key",
    title: "Service Key",
    badge: "Simple",
    link: "https://app.hubspot.com/l/service-keys",
    linkLabel: "Open Service Keys",
    steps: [
      "Settings (gear) → Integrations → Service Keys.",
      "Click “Create service key” and give it a name.",
      "Grant the same read scopes (deals, owners, contacts).",
      "Copy the generated key.",
    ],
  },
  {
    id: "personal-access-key",
    title: "Personal Access Key",
    badge: "Advanced",
    link: "https://app.hubspot.com/l/personal-access-key",
    linkLabel: "Open Personal Access Key",
    steps: [
      "Go to hubspot.com/l/personal-access-key (or Settings → your profile).",
      "Select the account and the read scopes you want to grant.",
      "Generate and copy the Personal Access Key.",
      "Note: keys are exchanged for a short-lived token on each sync automatically.",
    ],
  },
];

// Precompute the scope splits once (module scope) — they derive from constant
// data and never change, so there's no need to filter on every render.
const MIN_SCOPE_LIST = ALL_SCOPES.filter((s) => MIN_SCOPES.has(s));
const OPTIONAL_SCOPE_LIST = ALL_SCOPES.filter((s) => !MIN_SCOPES.has(s));


export default function Connect({ onConnected, status, onGoToAnalytics }) {
  const [credential, setCredential] = useState("");
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [scopesExpanded, setScopesExpanded] = useState(false);
  const [saveKey, setSaveKey] = useState(false); // opt-in: save key to account
  const [savedKeyInfo, setSavedKeyInfo] = useState(null); // {has_saved_key, key_preview, ...}
  const [connectingSaved, setConnectingSaved] = useState(false);
  const [savedKeyError, setSavedKeyError] = useState(null); // last error message (for Retry)
  const cancelSavedRef = useRef(false); // set true to cancel an in-flight saved-key connect

  // On load (logged-in user), check whether they have a saved HubSpot key.
  const loadSavedKey = useCallback(async () => {
    try {
      const info = await api.getSavedKey();
      setSavedKeyInfo(info);
    } catch (e) {
      setSavedKeyInfo(null); // not logged in / none
    }
  }, []);

  useEffect(() => {
    if (!status?.connected) loadSavedKey();
  }, [status?.connected, loadSavedKey]);

  const connected = !!status?.connected;
  const isCsv = status?.source === "csv";
  const connectedLabel = isCsv
    ? `CSV upload: ${status?.portal_info?.filename || "file"}`
    : status?.portal_info?.portalId
    ? `HubSpot portal ${status.portal_info.portalId}`
    : "HubSpot";
  const dealCount = status?.counts?.deals;

  const handleConnect = async () => {
    if (!credential.trim()) {
      toast.error("Please paste a HubSpot credential.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.connect(credential.trim(), saveKey);
      const label =
        res.auth_type === "pak" ? "Personal Access Key" : "credential";
      toast.success(`Connected to HubSpot via ${label}. Syncing your data…`);
      if (res.saved) toast.success("Key saved to your account for next time.");
      const sync = await runSync();
      if (sync.warnings?.length) sync.warnings.forEach((w) => toast.warning(w));
      toast.success(`Synced ${sync.counts.deals} deals from HubSpot.`);
      onConnected();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed.");
    } finally {
      setLoading(false);
    }
  };

  // Connect using the key already saved on the user's account (no re-paste).
  // Cancellable: the user can abort the (possibly long) sync; on failure we keep
  // an error so a Retry button can re-run it.
  const handleConnectSaved = async () => {
    cancelSavedRef.current = false;
    setSavedKeyError(null);
    setConnectingSaved(true);
    try {
      const res = await api.connectSaved();
      if (cancelSavedRef.current) throw new SyncCancelledError();
      const label = res.auth_type === "pak" ? "Personal Access Key" : "credential";
      toast.success(`Connected to HubSpot via saved ${label}. Syncing…`);
      const sync = await runSync(undefined, () => cancelSavedRef.current);
      if (sync.warnings?.length) sync.warnings.forEach((w) => toast.warning(w));
      toast.success(`Synced ${sync.counts.deals} deals from HubSpot.`);
      onConnected();
    } catch (e) {
      if (e instanceof SyncCancelledError || e?.cancelled) {
        // User cancelled — leave the saved-key panel ready to retry, no error.
        toast.message("Connection cancelled.");
      } else {
        const msg = e.response?.data?.detail || e.message || "Could not connect with your saved key.";
        setSavedKeyError(msg);
        toast.error(msg);
      }
    } finally {
      setConnectingSaved(false);
    }
  };

  // Cancel an in-flight saved-key connect/sync (stops client polling).
  const handleCancelSaved = () => {
    cancelSavedRef.current = true;
    setConnectingSaved(false);
  };

  const handleForgetKey = async () => {
    try {
      await api.forgetSavedKey();
      setSavedKeyInfo({ has_saved_key: false });
      toast.success("Saved key forgotten.");
    } catch (e) {
      logError("forget key failed:", e);
      toast.error("Could not forget the saved key.");
    }
  };

  const handleCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvLoading(true);
    try {
      const res = await api.uploadCsv(file);
      toast.success(`Loaded ${res.counts.deals} deals from CSV.`);
      onConnected();
    } catch (err) {
      toast.error(err.response?.data?.detail || "CSV upload failed.");
    } finally {
      setCsvLoading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <div className="relative">
        {/* Hero */}
        <div className="text-center mb-10">
          {!connected && (
            <>
              <img
                src={logo}
                alt="Senior Sales Analyst logo"
                className="w-20 h-20 object-contain mx-auto mb-4"
              />
              <h1 className="text-4xl font-bold tracking-tight text-foreground">
                SalesTap
              </h1>
              <p className="mt-3 text-lg text-muted-foreground max-w-4xl mx-auto text-balance">
                Connect your HubSpot CRM and get an AI-powered deep-dive on your pipeline,
                win rates, rep performance, and forecast — with an executive report written
                by a senior&nbsp;analyst.
              </p>
            </>
          )}
          <div className={`${connected ? "" : "mt-4"} inline-flex items-center gap-2 text-sm text-primary bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-full`}>
            <Lock className="w-4 h-4" />
            Read-only &amp; secure — we never see your password
          </div>
        </div>

        {/* Connected confirmation */}
        {connected && (
          <div
            data-testid="connect-connected-banner"
            className="mb-4 rounded-xl border border-green-500/30 bg-green-500/10 px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-foreground">
                  {isCsv ? "CSV data loaded" : "HubSpot connected"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {connectedLabel}
                  {typeof dealCount === "number" ? ` · ${dealCount} deals synced` : ""}
                  {status?.last_sync ? ` · ${new Date(status.last_sync).toLocaleString()}` : ""}
                </p>
              </div>
            </div>
            {onGoToAnalytics && (
              <Button
                data-testid="connect-go-analytics-btn"
                onClick={onGoToAnalytics}
                variant="outline"
                size="sm"
                className="flex-shrink-0"
              >
                <BarChart3 className="w-4 h-4 mr-1.5" />
                View Analytics
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            )}
          </div>
        )}

        {/* API-calls meter — how much of the daily HubSpot allotment was used */}
        {connected && (
          <div className="mb-8">
            <ApiUsage compact usage={status?.api_usage ?? null} />
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Instructions — 3 credential methods */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                Get a HubSpot credential
              </CardTitle>
              <CardDescription>
                Any of these read-only credentials works — pick whichever is easiest for you.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Accordion type="single" collapsible>
                {METHODS.map((m) => (
                  <AccordionItem key={m.id} value={m.id}>
                    <AccordionTrigger className="text-sm">
                      <span className="flex items-center gap-2">
                        {m.title}
                        <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary rounded px-1.5 py-0.5">
                          {m.badge}
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-2">
                        {m.steps.map((s, i) => (
                          <li key={s} className="flex gap-2 text-sm text-foreground/80">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-[11px] flex items-center justify-center font-medium">
                              {i + 1}
                            </span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ol>
                      <a
                        href={m.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-3"
                      >
                        {m.linkLabel} <ArrowRight className="w-3.5 h-3.5" />
                      </a>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <div className="rounded-lg bg-muted border border-border p-3" data-testid="scopes-panel">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                  Read scopes to grant
                </p>
                <p className="text-[11px] text-muted-foreground mb-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-primary align-middle mr-1" />
                  Highlighted scopes are the <span className="font-medium text-foreground">minimum required</span> to run an analysis.
                  Granting more (optional) lets SalesTap inventory additional CRM objects.
                </p>

                {/* Minimum scopes — always shown, highlighted */}
                <div className="flex flex-wrap gap-1.5" data-testid="scopes-min">
                  {MIN_SCOPE_LIST.map((s) => (
                    <code
                      key={s}
                      className="text-[11px] bg-primary/10 border border-primary/30 text-primary rounded px-1.5 py-0.5 font-medium"
                    >
                      {s}
                    </code>
                  ))}
                </div>

                {/* Optional scopes — collapsed by default */}
                {scopesExpanded && (
                  <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-border" data-testid="scopes-optional">
                    {OPTIONAL_SCOPE_LIST.map((s) => (
                      <code
                        key={s}
                        className="text-[11px] bg-background border border-border rounded px-1.5 py-0.5 text-foreground/80"
                      >
                        {s}
                      </code>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  data-testid="scopes-toggle"
                  onClick={() => setScopesExpanded((v) => !v)}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                >
                  {scopesExpanded ? (
                    <>
                      <ChevronUp className="w-3.5 h-3.5" /> Show fewer scopes
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3.5 h-3.5" /> Show all {ALL_SCOPES.length} supported scopes
                    </>
                  )}
                </button>

                <p className="text-[11px] text-muted-foreground mt-2">
                  The <span className="font-medium">calls / meetings / tasks / notes</span> and{" "}
                  <span className="font-medium">sales-email-read</span> scopes power the Sales Rep
                  Activity section — without them, activity counts show as zero.
                </p>
                <p className="text-[11px] text-muted-foreground mt-2">
                  <span className="font-medium text-foreground">Tip for granular call/activity analysis:</span>{" "}
                  the granular activity scopes (e.g. <code className="text-[10px]">crm.objects.calls.read</code>)
                  are most reliably available on a HubSpot <span className="font-medium">Private App</span> token —
                  Service Keys often can&rsquo;t select them. If you can only grant{" "}
                  <code className="text-[10px]">crm.objects.contacts.read</code>, SalesTap will fall back to
                  HubSpot&rsquo;s legacy engagements API to still capture your calls, tasks, meetings and notes.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Connect */}
          <div className="space-y-6">
            {/* Saved-key panel — one-click connect for returning users who chose
                to save their key (raw key never leaves the server). */}
            {!connected && savedKeyInfo?.has_saved_key && (
              <Card className="border-primary/30 bg-primary/5" data-testid="saved-key-panel">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                    Use your saved HubSpot key
                  </CardTitle>
                  <CardDescription>
                    We have a saved key on your account ({savedKeyInfo.key_preview || "••••"}). Connect without re-pasting.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {connectingSaved ? (
                    <>
                      <Button
                        data-testid="use-saved-key-btn"
                        disabled
                        className="w-full"
                      >
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting &amp; syncing…
                      </Button>
                      <Button
                        data-testid="cancel-saved-key-btn"
                        onClick={handleCancelSaved}
                        variant="outline"
                        className="w-full"
                      >
                        <X className="w-4 h-4 mr-2" /> Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      data-testid="use-saved-key-btn"
                      onClick={handleConnectSaved}
                      className="w-full"
                    >
                      {savedKeyError ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" /> Retry connect with saved key
                        </>
                      ) : (
                        <>
                          <KeyRound className="w-4 h-4 mr-2" /> Connect with saved key
                        </>
                      )}
                    </Button>
                  )}
                  {savedKeyError && !connectingSaved && (
                    <p className="text-xs text-destructive" data-testid="saved-key-error">
                      {savedKeyError}
                    </p>
                  )}
                  <button
                    type="button"
                    data-testid="forget-saved-key-btn"
                    onClick={handleForgetKey}
                    disabled={connectingSaved}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Forget saved key
                  </button>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-primary" />
                  {savedKeyInfo?.has_saved_key ? "Use a different key" : "Connect HubSpot"}
                </CardTitle>
                <CardDescription>
                  Paste a Private App token, Service Key, or Personal Access Key — we detect it automatically.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  data-testid={TID.tokenInput}
                  type="password"
                  placeholder="Paste your HubSpot token / key here"
                  value={credential}
                  onChange={(e) => setCredential(e.target.value)}
                  className="font-mono text-sm"
                />
                {/* Opt-in: save this key to the account (default OFF = use once). */}
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <Checkbox
                    data-testid="save-key-checkbox"
                    checked={saveKey}
                    onCheckedChange={(v) => setSaveKey(!!v)}
                    className="mt-0.5"
                  />
                  <span className="text-xs text-foreground/80">
                    <span className="font-medium">Save this key to my account</span> for next time (encrypted).
                    Leave unchecked to use it once — it&rsquo;s cleared when you disconnect or log out.
                  </span>
                </label>
                <Button
                  data-testid={TID.connectBtn}
                  onClick={handleConnect}
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connecting &amp; syncing…
                    </>
                  ) : (
                    "Connect & Analyze"
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Your credential is used read-only to fetch your CRM data for analysis. If you don&rsquo;t
                  save it, it is not retained after you disconnect or log out.
                </p>
                <p className="text-xs text-muted-foreground/80">
                  Note: the initial sync and analysis may take up to 2 minutes or more,
                  depending on the size of your CRM data.
                </p>
              </CardContent>
            </Card>

            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Upload className="w-4 h-4 text-primary" />
                  No credential? Upload a CSV instead
                </CardTitle>
                <CardDescription>
                  Export deals from HubSpot (or any CRM) and upload the CSV.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <label className="block">
                  <input
                    data-testid={TID.csvInput}
                    type="file"
                    accept=".csv"
                    onChange={handleCsv}
                    disabled={csvLoading}
                    className="block w-full text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-muted file:text-foreground hover:file:bg-border cursor-pointer"
                  />
                </label>
                {csvLoading && (
                  <p className="mt-2 text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
